import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faBars, faDownload, faEraser, faMagnifyingGlass, faPlus, faRefresh, faCaretDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import './Adventure.css';
import backend from './backend';
import ImageDialog from './image/ImageDialog';
import ItemBox from './itembox/ItemBox';
import Search from './Search';
import OpenItemsDialog from './OpenItemsDialog';
import { SquiggleItem } from './types/types';
import GraphView from './graph/GraphView';
import SideMenu from './sidemenu/SideMenu';
import { useOpenItemsContext } from './context/OpenItemsContext';
import { useAdventureDataContext } from './context/AdventureDataContext';
import { GraphViewSettingsProvider } from './context/GraphViewSettingsContext';
import { useItemCrudGlue } from './glueHooks/useItemCrudGlue';
import { useItemStatusGlue } from './glueHooks/useItemStatusGlue';
import { useLinkGlue } from './glueHooks/useLinkGlue';
import { useAliasGlue } from './glueHooks/useAliasGlue';
import { useImageGlue } from './glueHooks/useImageGlue';
import { useExportGlue } from './glueHooks/useExportGlue';
import { useAddItemGlue } from './glueHooks/useAddItemGlue';
import { usePinnedAreaGlue } from './glueHooks/usePinnedAreaGlue';
import { useAdventureSyncGlue } from './glueHooks/useAdventureSyncGlue';
import NewItemMenu from './NewItemMenu';
import { systemItems } from './biz/systemItems';
import { faCircleQuestion } from '@fortawesome/free-regular-svg-icons';
import { useItemSettingsGlue } from './glueHooks/useItemSettingsGlue';

let lastKnownSystemStatus = '';

function Adventure() {
  const params = useParams();
  const location = useLocation(); // Get the current location
  const adventureId = params.id!;
  const {
    reloadItems,
    adventureLoaded,
    adventureName,
    itemById,
  } = useAdventureDataContext();
  const {
    itemsOpen,
    addOpenItem,
    closeOpenItem,
    clearOpenNonEditItems,
    setEditProtection,
    reconstituteOpenItems,
    hasEditProtection,
    editProtectionIds,
    selectedItemId,
    setSelectedItemId
  } = useOpenItemsContext();
  const [appVersion, setAppVersion] = useState<string>('');
  const [showUpgradeMessage, setShowUpgradeMessage] = useState<boolean>(false);
  const [systemStatusMessage, setSystemStatusMessage] = useState<string>('');
  const [imageDialogState, setImageDialogState] = useState<{ item: SquiggleItem, imageIndex: number }|undefined>();
  const [flashValues, setFlashValues] = useState<{ [key: string]: { type: 'none'|'subtle'|'strong' } }>({});
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [showOpenItemsDialog, setShowOpenItemsDialog] = useState<boolean>(false);
  const [showGraphView, setShowGraphView] = useState<boolean>(false);
  const [showNewItemMenu, setShowNewItemMenu] = useState<boolean>(false);
  const [showToolbarMenu, setShowToolbarMenu] = useState<boolean>(false);
  const pendingNewItemNameRef = useRef<string>('');
  const toolbarMenuRef = useRef<HTMLDivElement | null>(null);
  const {
    pinnedItems,
    pinnedItemsSet,
    pinnedAreaWidth,
    isResizingPinnedArea,
    itemAreaRef,
    addPinnedItem,
    removePinnedItem,
    retainPinnedItems,
    startResizePinnedArea
  } = usePinnedAreaGlue();

  const { addQuest, addPersonOrOrg, addPlace, addMiscItem, addLogEntry, addNewItem } = useAddItemGlue();

  const getCurrentSelectedText = useCallback(() => {
    const activeElement = document.activeElement as HTMLInputElement | HTMLTextAreaElement | null;
    let selectedText = '';

    if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
      const start = activeElement.selectionStart ?? 0;
      const end = activeElement.selectionEnd ?? 0;
      if (start !== end) {
        selectedText = activeElement.value.substring(start, end);
      }
    }

    if (!selectedText) {
      selectedText = window.getSelection()?.toString() ?? '';
    }

    return selectedText.replace(/\s+/g, ' ').trim();
  }, []);

  const preparePendingNewItemName = useCallback(() => {
    pendingNewItemNameRef.current = getCurrentSelectedText();
  }, [getCurrentSelectedText]);

  const consumePendingNewItemName = useCallback(() => {
    const pendingName = pendingNewItemNameRef.current;
    pendingNewItemNameRef.current = '';
    return pendingName;
  }, []);

  const openNewItemMenu = useCallback(() => {
    setShowToolbarMenu(false);
    preparePendingNewItemName();
    setShowNewItemMenu(true);
  }, [preparePendingNewItemName]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (toolbarMenuRef.current && !toolbarMenuRef.current.contains(event.target as Node)) {
        setShowToolbarMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const showLostConnectionStatus = useCallback(() => {
    if (!lastKnownSystemStatus || lastKnownSystemStatus === '') {
      setSystemStatusMessage("Lost connection. Your internet connectivity might be flaky, or SquiggleBoard might be undergoing maintenance.");
    } else {
      setSystemStatusMessage("Lost connection. Last known status: " + lastKnownSystemStatus);
    }
  }, [setSystemStatusMessage]);

  const refreshAll = useCallback(async () => {
    try {
      setShowToolbarMenu(false);
      const items = await reloadItems();
      reconstituteOpenItems(items.quests, items.peopleAndOrgs, items.places, items.misc, items.logEntries, systemItems);
    } catch (error) {
      console.error('Failed to refresh adventure data:', error);
      showLostConnectionStatus();
    }
  }, [reloadItems, reconstituteOpenItems, showLostConnectionStatus]);

  const openItem = useCallback((itemId: string, flashType: 'none'|'subtle'|'strong') => {
    const item = itemById.get(itemId);
    if (item) {
      if (flashType !== 'none') {
        setFlashValues((flashValues) => ({ ...flashValues, [item.id]: { type: flashType } }));
      }
      addOpenItem(item);
    }
  }, [itemById, addOpenItem, setFlashValues]);

  const closeItem = useCallback((itemId: string) => {
    const index = itemsOpen.findIndex(item => item.id === itemId);
    closeOpenItem(itemId);
    removePinnedItem(itemId);
    if (selectedItemId === itemId) {
      if (itemsOpen.length > 1) {
        const nextIndex = (index === itemsOpen.length - 1) ? index - 1 : index + 1;
        setSelectedItemId(itemsOpen[nextIndex].id);
      } else {
        setSelectedItemId(null);
      }
    }
  }, [closeOpenItem, removePinnedItem, selectedItemId, setSelectedItemId, itemsOpen]);

  const clearItems = useCallback(() => {
    setShowToolbarMenu(false);
    clearOpenNonEditItems();
    retainPinnedItems(editProtectionIds);
  }, [clearOpenNonEditItems, retainPinnedItems, editProtectionIds]);

  const pinItem = useCallback((itemId: string, pinned: boolean) => {
    addPinnedItem(itemId, pinned);
    setFlashValues((flashValues) => ({ ...flashValues, [itemId]: { type: 'subtle' } }));
  }, [addPinnedItem, setFlashValues]);

  const handleOpenItemFromGraph = useCallback((itemId: string) => {
    openItem(itemId, 'subtle');
  }, [openItem]);

  const openSearch = useCallback(() => {
    setShowToolbarMenu(false);
    setShowSearch(true);
  }, []);

  const openHelp = useCallback(() => {
    setShowToolbarMenu(false);
    openItem(systemItems[0].id, 'subtle');
  }, [openItem]);

  const modalDialogOpen = showGraphView || showSearch || showNewItemMenu || showOpenItemsDialog || imageDialogState !== undefined;

  useEffect(() => {
    const keyHandler = (e: KeyboardEvent) => {
      if (modalDialogOpen) {
        return;
      }
      if (e.shiftKey && (e.ctrlKey || e.metaKey)) {
        if (e.key === 'F' || e.key === "f") {
          setShowSearch(true);
          e.preventDefault();
        }
        if (e.key === 'O' || e.key === "o") {
          setShowOpenItemsDialog(true);
          e.preventDefault();
        }
        if (e.key === 'D' || e.key === "d") {
          clearOpenNonEditItems();
          e.preventDefault();
        }
        if (e.key === 'G' || e.key === "g") {
          setShowGraphView((previous) => !previous);
          e.preventDefault();
        }
        if (e.key === 'I' || e.key === "i") {
          openNewItemMenu();
          e.preventDefault();
        }
      } else if ((e.key === ',' || e.key === '.') && e.ctrlKey) {
        if (itemsOpen.length > 0) {
          const currentIndex = itemsOpen.findIndex(item => item.id === selectedItemId);
          const nextIndex = e.key === ',' ? (currentIndex - 1 + itemsOpen.length) % itemsOpen.length : (currentIndex + 1) % itemsOpen.length;
          setSelectedItemId(itemsOpen[nextIndex].id);
          e.preventDefault();
        }
      }
    };
    window.addEventListener("keydown", keyHandler);
    return () => {
      window.removeEventListener("keydown", keyHandler);
    }
  }, [clearOpenNonEditItems, itemsOpen, selectedItemId, setSelectedItemId, modalDialogOpen, openNewItemMenu]);

  useEffect(() => {
    document.title = `${adventureName} - SquiggleBoard`;
  }, [adventureName]);

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const response = await fetch("/version.txt");
        if (response.ok && response.headers.get('Content-Type')?.startsWith('text/plain')) {
          const version = (await response.text()).trim();
          if (showUpgradeMessage || (appVersion !== '' && version !== appVersion)) {
            setShowUpgradeMessage(true);
          }
          setAppVersion(version);
          return;
        } else {
          console.error('Failed to fetch app version');
        }
      } catch (error) {
        console.error('Error fetching app version:', error);
      }
    };

    fetchVersion();
    const intervalId = setInterval(fetchVersion, 10 * 60 * 1000); // 10 minutes

    return () => clearInterval(intervalId);
  }, [setShowUpgradeMessage, showUpgradeMessage, appVersion, setAppVersion]);

  useEffect(() => {
    const fetchSystemStatus = async () => {
      try {
        const response = await fetch("/system-status.txt");
        if (response.ok && response.headers.get('Content-Type')?.startsWith('text/plain')) {
          const statusText = await response.text();
          lastKnownSystemStatus = statusText;
          setSystemStatusMessage(lastKnownSystemStatus);
          return;
        } else {
          console.error('Failed to fetch system status');
        }
      } catch (error) {
        console.error('Error fetching system status:', error);
      }
      showLostConnectionStatus();
    };

    fetchSystemStatus();
    const intervalId = setInterval(fetchSystemStatus, 2 * 60 * 1000); // 2 minutes
    window.addEventListener('online', fetchSystemStatus);
    window.addEventListener('offline', fetchSystemStatus);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('online', fetchSystemStatus);
      window.removeEventListener('offline', fetchSystemStatus);
    };
  }, [showLostConnectionStatus, setSystemStatusMessage]);

  useEffect(() => {
    const beforeUnloadHandler = (e: BeforeUnloadEvent) => {
      if (!hasEditProtection()) {
        return;
      }
      const message = 'Are you sure? Unsaved changes will be lost.';
      e.preventDefault();
      e.returnValue = message;
      return message;
    };

    window.addEventListener('beforeunload', beforeUnloadHandler);
    return () => window.removeEventListener('beforeunload', beforeUnloadHandler);
  }, [hasEditProtection]);

  // Some magic to keep openItem from triggering the second useEffect
  const openItemRef = useRef(openItem);
  useEffect(() => {
    openItemRef.current = openItem;
  }, [openItem]);
  useEffect(() => {
    const anchorItemId = location.hash.slice(1);
    if (anchorItemId) {
      openItemRef.current(anchorItemId, 'none');
      setSelectedItemId(anchorItemId);
    } else if (!adventureLoaded) {
      openItemRef.current(systemItems[5].id, 'none');
    }
  }, [location.hash, setSelectedItemId, adventureLoaded]);

  const { createItem, deleteItem } = useItemCrudGlue(adventureId);
  const { toggleArchiveItem, toggleStarItem } = useItemStatusGlue(adventureId, refreshAll);
  const { addLink, removeLink, setParent } = useLinkGlue(adventureId, refreshAll);
  const { addAlias, removeAlias } = useAliasGlue(adventureId, refreshAll);
  const { setIgnoreCanonicalName, setCaseSensitiveNames } = useItemSettingsGlue(adventureId, refreshAll);
  const { uploadImage, removeImage, addMarker, removeMarker } = useImageGlue(adventureId, refreshAll, imageDialogState, setImageDialogState);
  const { exportData } = useExportGlue(adventureId, adventureName);
  useAdventureSyncGlue(adventureId, refreshAll, showLostConnectionStatus);

  const renderItemBox = useCallback((item: SquiggleItem, isPinned: boolean) => {
    return <ItemBox
      key={item.id}
      item={item}
      isPinned={isPinned}
      pinItem={pinItem}
      close={closeItem}
      create={createItem}
      refresh={refreshAll}
      setEditProtection={setEditProtection}
      deleteItem={deleteItem}
      toggleArchiveItem={toggleArchiveItem}
      toggleStarItem={toggleStarItem}
      link={(itemId, selectItem) => { openItem(itemId, 'subtle'); if (selectItem) { setSelectedItemId(itemId); } }}
      addLink={addLink}
      removeLink={removeLink}
      setParent={setParent}
      addAlias={addAlias}
      removeAlias={removeAlias}
      setIgnoreCanonicalName={setIgnoreCanonicalName}
      setCaseSensitiveNames={setCaseSensitiveNames}
      uploadImage={uploadImage}
      removeImage={removeImage}
      imageSeed={backend.getImageSeed()}
      zoomImage={(item, imageIndex) => { setImageDialogState({ item, imageIndex }) }}
      addNewChild={(parentId, type) => addNewItem(type, parentId)}
      flash={flashValues[item.id]}>
    </ItemBox>;
  }, [
    pinItem,
    closeItem,
    createItem,
    refreshAll,
    setEditProtection,
    deleteItem,
    toggleArchiveItem,
    toggleStarItem,
    openItem,
    setSelectedItemId,
    addLink,
    removeLink,
    setParent,
    addAlias,
    removeAlias,
    setIgnoreCanonicalName,
    setCaseSensitiveNames,
    uploadImage,
    removeImage,
    addNewItem,
    flashValues
  ]);

  return <div id="adventurepage">
    <SideMenu
      addPrepare={preparePendingNewItemName}
      addQuest={() => addQuest(undefined, consumePendingNewItemName())}
      addPersonOrOrg={() => addPersonOrOrg(undefined, consumePendingNewItemName())}
      addPlace={() => addPlace(undefined, consumePendingNewItemName())}
      addMiscItem={() => addMiscItem(undefined, consumePendingNewItemName())}
      addLogEntry={() => addLogEntry(undefined, consumePendingNewItemName())}
      openItem={(itemId: string) => { setSelectedItemId(itemId); openItem(itemId, 'subtle'); }}
    />
    <div id="mainarea">
      <div id="topbar">
        <div id="adventurename">{adventureName}</div>
        <div id="toolbar">
          <div className="toolbarsegmented">
            <button
              type="button"
              className={`toolbarsegment ${!showGraphView ? 'active' : ''}`}
              title="Board view [Toggle: Shift+Ctrl/Command+G]"
              onClick={() => setShowGraphView(false)}>
              Board
            </button>
            <button
              type="button"
              className={`toolbarsegment ${showGraphView ? 'active' : ''}`}
              title="Graph view [Toggle: Shift+Ctrl/Command+G]"
              onClick={() => setShowGraphView(true)}>
              Graph
            </button>
          </div>
          <div style={{ width: 8 }} /> {/* Spacer */}
          <div className="toolbarbuttonsfull">
            <div
              className="toolbarbutton"
              title="Add item [Shift+Ctrl/Command+I]"
              onClick={openNewItemMenu}
            >
              <FontAwesomeIcon icon={faPlus as IconProp} />
            </div>
            <div className="toolbarbutton" title="Refresh all" onClick={refreshAll}><FontAwesomeIcon icon={faRefresh as IconProp} /></div>
            <div className="toolbarbutton" title="Clear workspace [Shift+Ctrl/Command+D]" onClick={clearItems}><FontAwesomeIcon icon={faEraser as IconProp} /></div>
            <div className="toolbarbutton" title="Search [Shift+Ctrl/Command+F]" onClick={openSearch}><FontAwesomeIcon icon={faMagnifyingGlass as IconProp} /></div>
            <div className="toolbarbutton" title="Download" onClick={exportData}><FontAwesomeIcon icon={faDownload as IconProp} /></div>
            <div className="toolbarbutton" title="App info" onClick={openHelp}><FontAwesomeIcon icon={faCircleQuestion as IconProp} /></div>
          </div>
          <div className="toolbarcompact" ref={toolbarMenuRef}>
            <div
              className="toolbarbutton toolbarcompacttrigger"
              title="Menu"
              aria-label="Open toolbar menu"
              aria-expanded={showToolbarMenu}
              onClick={() => setShowToolbarMenu((current) => !current)}
            >
              <FontAwesomeIcon icon={faBars as IconProp} />
              <FontAwesomeIcon icon={faCaretDown as IconProp} />
            </div>
            {showToolbarMenu && (
              <div className="toolbardropdown">
                <button type="button" className="toolbardropdownitem" onClick={openNewItemMenu}><FontAwesomeIcon icon={faPlus as IconProp} /> Add item</button>
                <button type="button" className="toolbardropdownitem" onClick={refreshAll}><FontAwesomeIcon icon={faRefresh as IconProp} /> Refresh all</button>
                <button type="button" className="toolbardropdownitem" onClick={clearItems}><FontAwesomeIcon icon={faEraser as IconProp} /> Clear workspace</button>
                <button type="button" className="toolbardropdownitem" onClick={openSearch}><FontAwesomeIcon icon={faMagnifyingGlass as IconProp} /> Search</button>
                <button type="button" className="toolbardropdownitem" onClick={exportData}><FontAwesomeIcon icon={faDownload as IconProp} /> Download</button>
                <button type="button" className="toolbardropdownitem" onClick={openHelp}><FontAwesomeIcon icon={faCircleQuestion as IconProp} /> Help</button>
              </div>
            )}
          </div>
        </div>
      </div>
      { showUpgradeMessage && <div id="upgrademessage">You are using an older version of the app. Please save your work and reload the page to upgrade to the latest version. Unsaved data will be lost.</div> }
      <div id="itemarea" ref={itemAreaRef}>
        <div id="unpinnedarea" className={showGraphView ? 'graph-mode' : ''} onClick={() => { if (!showGraphView) { setSelectedItemId(null); } }}>
          <div className={`boardview${showGraphView ? ' hidden' : ''}`} aria-hidden={showGraphView}>
            {itemsOpen.filter(item => !pinnedItemsSet.has(item.id)).map(item => renderItemBox(item, false))}
          </div>
          { !showGraphView &&
            <>
              <div style={{ flexGrow: 1 }} />
              <div className="appname">SquiggleBoard</div>
            </>
          }
          <GraphViewSettingsProvider>
            {showGraphView && (
              <div className="graphviewhost">
                <GraphView
                  itemsOpen={itemsOpen}
                  selectedItemId={selectedItemId}
                  setSelectedItemId={setSelectedItemId}
                  openItem={handleOpenItemFromGraph}
                />
                <div className="appname">SquiggleBoard</div>
              </div>
            )}
          </GraphViewSettingsProvider>
        </div>
        <div
          id="pinnedarea"
          style={{ width: pinnedAreaWidth }}
          className={`${pinnedItems.length === 0 ? 'no-pinned-items' : ''}${isResizingPinnedArea ? ' resizing' : ''}`.trim()}>
          { pinnedItems.length > 0 && <div id="pinnedarearesizehandle" onMouseDown={startResizePinnedArea} /> }
          {itemsOpen.filter(item => pinnedItemsSet.has(item.id)).map(item => renderItemBox(item, true))}
        </div>
      </div>
      { systemStatusMessage && <div id="systemstatus">{systemStatusMessage}</div> }
    </div>
    { imageDialogState && <ImageDialog item={imageDialogState.item} imageIndex={imageDialogState.imageIndex} imageSeed={backend.getImageSeed()} openItem={(itemId) => openItem(itemId, 'subtle')} setSelectedItemId={setSelectedItemId} close={() => setImageDialogState(undefined)} addMarker={addMarker} removeMarker={removeMarker}></ImageDialog> }
    { showSearch && <Search openItem={(itemId) => openItem(itemId, 'strong')} close={(lastOpenedItemId: string|null) => { setShowSearch(false); lastOpenedItemId && setSelectedItemId(lastOpenedItemId); }}></Search> }
    { showOpenItemsDialog && <OpenItemsDialog openItem={(itemId) => openItem(itemId, 'strong')} closeItem={closeItem} close={(lastFocusedItemId: string|null) => { setShowOpenItemsDialog(false); lastFocusedItemId && setSelectedItemId(lastFocusedItemId); }}></OpenItemsDialog> }
    { showNewItemMenu && <NewItemMenu newQuest={() => addQuest(undefined, consumePendingNewItemName())} newPersonOrOrg={() => addPersonOrOrg(undefined, consumePendingNewItemName())} newPlace={() => addPlace(undefined, consumePendingNewItemName())} newMiscItem={() => addMiscItem(undefined, consumePendingNewItemName())} newLogEntry={() => addLogEntry(undefined, consumePendingNewItemName())} close={(lastOpenedItemId: string|null) => { setShowNewItemMenu(false); lastOpenedItemId && lastOpenedItemId && setSelectedItemId(lastOpenedItemId); }}></NewItemMenu> }
  </div>
}

export default Adventure;